/**
 * Cómo divide cada cliente sus galpones.
 *
 * No hay una única forma en el negocio, por eso es configuración: unos meten un
 * solo lote por galpón, otros lo tienen partido con muros y trabajan cada parte
 * como una unidad, y otros lo dividen con paneles que cambian entre ciclos.
 *
 * Lo que decide dónde vive el dato es CUÁNDO cambia la división: lo permanente
 * puede ser estructura, lo que cambia por ciclo no — si se editara cada ciclo,
 * los registros anteriores pasarían a apuntar a algo que ya significa otra cosa.
 *
 * Espejo de `AviarBack/src/utils/division-galpon.ts`.
 */

export const MODOS_DIVISION = ['galpon', 'secciones', 'divisiones'] as const

export type ModoDivision = (typeof MODOS_DIVISION)[number]

export const MODO_DIVISION_POR_DEFECTO: ModoDivision = 'galpon'

export const MODO_DIVISION_LABEL: Record<ModoDivision, string> = {
  galpon: 'Un lote por galpón',
  secciones: 'Secciones fijas (muro o malla)',
  divisiones: 'Divisiones móviles por ciclo',
}

export const MODO_DIVISION_AYUDA: Record<ModoDivision, string> = {
  galpon: 'El galpón no se divide: la capacidad y el área son las suyas.',
  secciones:
    'El galpón está partido de forma permanente. Cada sección lleva su área y se planea sobre ellas.',
  divisiones:
    'La división cambia entre ciclos, así que no es estructura: el área que ocupa cada lote se captura al encasetar.',
}

export function normalizarModoDivision(valor: unknown): ModoDivision {
  const modo = String(valor ?? '').trim()
  return (MODOS_DIVISION as readonly string[]).includes(modo)
    ? (modo as ModoDivision)
    : MODO_DIVISION_POR_DEFECTO
}

/** true si el reparto baja al nivel de sección en vez de quedarse en el galpón. */
export const repartePorSeccion = (modo: ModoDivision): boolean => modo === 'secciones'

/** true si el área que ocupa el lote se captura al encasetar, por ser móvil. */
export const areaSeCapturaAlEncasetar = (modo: ModoDivision): boolean => modo === 'divisiones'
